import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { Button } from '@/components/ui/button';
import { Add } from '@mui/icons-material';
import { Ellipsis } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ButtonLoading } from '@/components/ui/button';
import { CityKnowledgeDataTable } from './table/data-table';
import { cityKnowledgeColumns, type CityKnowledgeItem } from './table/columns';
import { CityKnowledgeTableLoading } from './table/loading';
import { AddDialog, type CityKnowledgeFormValues } from './add-dialog';
import {
  QUERY_GET_CITY_KNOWLEDGE,
  QUERY_GET_AVAILABLE_CITIES,
} from '@/graphql/queries/chatKnowledge';
import {
  MUTATION_ADD_CITY_KNOWLEDGE,
  MUTATION_DELETE_CITY_KNOWLEDGE,
} from '@/graphql/mutation/chatKnowledge';
import type {
  CityKnowledgeListResponse,
  AvailableCity,
  CityKnowledge,
} from '@/graphql/types/chatKnowledge';

export const CityKnowledgeSection = () => {
  const apolloClient = useApolloClient();
  const { alert, showAlert, handleCloseAlert } = useAlert();

  const [loadingData, setLoadingData] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [data, setData] = useState<CityKnowledgeListResponse[]>([]);
  const [availableCities, setAvailableCities] = useState<AvailableCity[]>([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CityKnowledgeListResponse | null>(null);
  const [editingItem, setEditingItem] = useState<CityKnowledgeListResponse | null>(null);

  const normalizedData = useMemo(() => {
    return data.map((item) => ({
      id: item.id,
      title: item.title,
      municipality_code: item.municipality_code,
      municipality_name: item.municipality?.name_municipality,
      category: item.category,
      content_preview: item.content,
      created_at: item.created_at,
      actions: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="bottom" align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItem(item);
                setOpenRemoveDialog(true);
              }}
            >
              <span>Remover</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    })) as CityKnowledgeItem[];
  }, [data]);

  const getCityKnowledge = async () => {
    try {
      setLoadingData(true);
      const { data } = await apolloClient.query<{
        getCityKnowledge: CityKnowledgeListResponse[];
      }>({
        query: QUERY_GET_CITY_KNOWLEDGE,
        variables: {
          input: {
            limit: 100,
            offset: 0,
          },
        },
      });
      setData(data.getCityKnowledge);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingData(false);
    }
  };

  const getAvailableCities = async () => {
    try {
      const { data } = await apolloClient.query<{ getAvailableCities: AvailableCity[] }>({
        query: QUERY_GET_AVAILABLE_CITIES,
        variables: {
          limit: 6000,
          offset: 0,
        },
      });
      setAvailableCities(data.getAvailableCities);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    }
  };

  const handleAdd = async (values: CityKnowledgeFormValues) => {
    try {
      setLoadingForm(true);
      await apolloClient.mutate<{ addCityKnowledge: CityKnowledge }>({
        mutation: MUTATION_ADD_CITY_KNOWLEDGE,
        variables: {
          input: values,
        },
      });

      setOpenAddDialog(false);
      setEditingItem(null);
      showAlert('success', editingItem ? 'Conhecimento atualizado com sucesso' : 'Conhecimento adicionado com sucesso');
      await getCityKnowledge();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingForm(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedItem) return;

    try {
      setLoadingForm(true);
      await apolloClient.mutate({
        mutation: MUTATION_DELETE_CITY_KNOWLEDGE,
        variables: {
          id: selectedItem.id,
        },
      });

      setOpenRemoveDialog(false);
      showAlert('success', 'Conhecimento removido com sucesso');
      setSelectedItem(null);
      await getCityKnowledge();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingForm(false);
    }
  };

  useEffect(() => {
    getCityKnowledge();
    getAvailableCities();
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          variant="default"
          onClick={() => {
            setEditingItem(null);
            setOpenAddDialog(true);
          }}
        >
          <Add />
          Adicionar Conhecimento
        </Button>
      </div>

      {loadingData ? (
        <CityKnowledgeTableLoading />
      ) : (
        <CityKnowledgeDataTable
          columns={cityKnowledgeColumns}
          data={normalizedData}
          onRowClick={(row) => {
            const originalItem = data.find((d) => d.id === row.id);
            if (originalItem) {
              setEditingItem(originalItem);
              setOpenAddDialog(true);
            }
          }}
        />
      )}

      <AddDialog
        open={openAddDialog}
        onOpenChange={(open) => {
          setOpenAddDialog(open);
          if (!open) {
            setEditingItem(null);
          }
        }}
        onSubmit={handleAdd}
        loading={loadingForm}
        availableCities={availableCities}
        initialData={editingItem}
      />

      <Dialog open={openRemoveDialog} onOpenChange={setOpenRemoveDialog}>
        <DialogContent className="gap-6">
          <DialogHeader className="w-fit">
            <DialogTitle>Remover Conhecimento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja remover o conhecimento:{' '}
            <span className="font-semibold">{selectedItem?.title}</span>?
          </p>
          <DialogFooter className="flex-col">
            <Button
              variant="outline"
              onClick={() => {
                setOpenRemoveDialog(false);
                setSelectedItem(null);
              }}
            >
              Cancelar
            </Button>
            <ButtonLoading
              variant="default"
              label="Remover conhecimento"
              loading={loadingForm}
              onClick={handleRemove}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </div>
  );
};
